import type { PricingConfig } from "@repo/api-dtos"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { CACHE_STALE_TIME } from "@/config/constants"
import { useMarketplaceOrpc } from "@/integrations/orpc"

const pricingQueryKey = ["marketplace-admin", "pricing"] as const

export const pricingConfigQueryKey = [...pricingQueryKey, "config"] as const

/** Loads the active pricing config version (version 0 = code defaults, nothing saved yet). */
export function usePricingConfig({
	enabled = true,
}: {
	enabled?: boolean
} = {}) {
	const { client, isAuthReady } = useMarketplaceOrpc()

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: pricingConfigQueryKey,
		queryFn: () => client.configuration.pricing.get(),
		enabled: enabled && isAuthReady,
	})

	return { configVersion: data ?? null, isLoading, error, refetch }
}

export interface SavePricingConfigInput {
	config: PricingConfig
	/** The version the editor loaded. */
	baseVersion: number
}

/**
 * Saves the pricing config as the next version, with `baseVersion` as the
 * optimistic-concurrency base (`VERSION_CONFLICT` when someone saved first).
 */
export function useSavePricingConfig() {
	const { client } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: SavePricingConfigInput) =>
			client.configuration.pricing.save(input),
		onSuccess: async (result) => {
			await queryClient.invalidateQueries({ queryKey: pricingQueryKey })
			toast.success(
				`Pricing v${result.version} saved. Servers use it within a minute.`,
			)
		},
	})
}

/** Queues the Price check sweep now; `sendEmails` false only writes the snapshots. */
export function useRunPriceCheckSweep() {
	const { client } = useMarketplaceOrpc()

	return useMutation({
		mutationFn: (input: { sendEmails: boolean }) =>
			client.configuration.pricing.runPriceCheckSweep(input),
	})
}

/** The all-sellers preview, from the stored snapshots. */
export function usePricingPreviewAll({
	enabled = true,
}: {
	enabled?: boolean
}) {
	const { client, isAuthReady } = useMarketplaceOrpc()

	return useQuery({
		queryKey: [...pricingQueryKey, "preview-all"],
		queryFn: () => client.configuration.pricing.previewAll({ days: 14 }),
		enabled: enabled && isAuthReady,
		staleTime: CACHE_STALE_TIME.ENTITY_METADATA,
	})
}

/** The one-seller preview; runs only once a seller is picked. */
export function usePricingPreviewSeller(userId: string | null) {
	const { client, isAuthReady } = useMarketplaceOrpc()

	return useQuery({
		queryKey: [...pricingQueryKey, "preview-seller", userId],
		queryFn: () =>
			client.configuration.pricing.previewSeller({ userId: userId ?? "" }),
		enabled: isAuthReady && userId !== null,
		retry: false,
	})
}

/** Fails a stuck reprice run by id (superadmin). */
export function useForceFailRepriceRun() {
	const { client } = useMarketplaceOrpc()

	return useMutation({
		mutationFn: (input: { runId: string; reason: string }) =>
			client.reprice.forceFail(input),
	})
}

/** Publishes a reprice run's queue message again (superadmin). */
export function useRequeueRepriceRun() {
	const { client } = useMarketplaceOrpc()

	return useMutation({
		mutationFn: (input: { runId: string }) => client.reprice.requeue(input),
	})
}
